import PropTypes from "prop-types";
import RadialProgress from "../circularLoader";
import { InfoIcon, SealCheckIcon, SealWarningIcon, XIcon, CaretRightIcon } from "@phosphor-icons/react";
import useToastAnimation from "../../../customHooks/toast/useToastAnimation";
import useToastViewport from "../../../customHooks/toast/useToastViewport";
import {
  getToastAnimationClasses,
  getToastContainerClasses,
  getToastPlacement,
  getToastWidthClasses,
  splitToastPositionClasses,
} from "./toastAnimationUtils";

/**
 * Toast component for displaying toast notifications.
 * Layout, animation, and responsive positioning are handled internally unless `isInline` is set.
 *
 * @param {Object} props - The props object.
 * @param {string} props.variant - The variant of the toast (info, success, warning, error).
 * @param {string} props.subHeader="" - The subheader text for the toast.
 * @param {string} props.header - The header text for the toast.
 * @param {boolean} props.isToast - Flag indicating whether the toast is visible.
 * @param {Function} props.onToastChange=() => {} - Function to handle toast events.
 * @param {number} props.duration=5000 - Duration in milliseconds for which the toast will be displayed.
 * @param {string} props.position - Optional legacy layout/z-index classes.
 * @param {boolean} props.isCloseButton=false - Flag indicating whether to display a close button in the toast.
 * @param {boolean} props.isDrawer=false - Whether the toast is rendered inside a drawer.
 * @param {boolean} props.isMobile - Whether the toast is rendered on mobile.
 * @param {boolean} props.isInline=false - When true, toast renders inline without fixed/absolute container.
 * @param {"top"|"bottom"|"auto"} props.placement - Toast slide direction override.
 * @returns {JSX.Element|null} The JSX element representing the toast or null if the toast is not visible.
 */
const Toast = ({
  variant,
  subHeader = "",
  header,
  isToast,
  onToastChange = () => {},
  duration = 5000,
  isCloseButton = false,
  position = "",
  fill = "",
  strokeWidth,
  href = "",
  isDrawer = false,
  isMobile: isMobileProp,
  isInline = false,
  placement = "auto",
  viewText = "View",
  isShowCartEmpty = false,
}) => {
  const {
    rendered,
    phase,
    dismiss,
    handleTransitionEnd: onTransitionEndHandler,
  } = useToastAnimation(isToast, duration, onToastChange);
  const isMobile = useToastViewport(isMobileProp);
  const { content: contentPositionClasses } = splitToastPositionClasses(position);

  const resolvedPlacement = getToastPlacement({ isDrawer, isMobile, placement, position });
  const animationClasses = getToastAnimationClasses(phase, resolvedPlacement, isMobile);
  const widthClasses = getToastWidthClasses({ isDrawer, isMobile });
  const containerClasses = getToastContainerClasses({
    isDrawer,
    isMobile,
    resolvedPlacement,
    position,
    isInline,
  });

  const normalizedVariant = variant === "error" ? "warning" : variant;

  /**
   * close handler function to cancel a toast.
   * @returns {void}
   */
  const onClose = () => {
    if (isToast) {
      dismiss();
    }
  };

  const icons = {
    info: <InfoIcon size={24} weight="fill" color="#195EE0" />,
    success: <SealCheckIcon size={24} weight="fill" color="#29852F" />,
    warning: <SealWarningIcon size={24} weight="fill" color="#D14826" />,
  };

  /**
   * Get the icon in toast to display as notification icon.
   * @param {string} variation - The variation of the toast (info, success, warning, error).
   * @returns {JSX.Element|null} The JSX element representing the toast or null if the toast is not visible.
   */
  const getIcon = (variation) => {
    return icons[variation];
  };

  const variantBorderColors = {
    info: "border-[#759EEC] bg-[radial-gradient(circle_at_center,_#FFFFFF_0%,_#E8EFFC_100%)]",
    success: "border-[#72B277] bg-[radial-gradient(circle_at_center,_#ECFDF5_0%,_#CEEBD0_100%)]",
    warning: "border-[#E3917D] bg-[radial-gradient(circle_at_center,_#FAEDE9_0%,_#F6DAD4_100%)]",
  };

  const borderColor = variantBorderColors[normalizedVariant];

  if (!rendered) {
    return null;
  }

  const toastContent = (
    <div className={`w-full ${animationClasses} ${widthClasses}`} onTransitionEnd={onTransitionEndHandler}>
      <div
        id={normalizedVariant}
        className={`${contentPositionClasses} flex min-h-[56px] items-center justify-between gap-8 border-[0.5px] px-[10px] py-[12px] ${borderColor} rounded-[12px] shadow-md`}
        data-testid="toast"
      >
        <div className="flex items-center ">
          <div className="mr-8">{getIcon(normalizedVariant)}</div>
          <div>
            <p className="text-14 font-600">{header}</p>
            {subHeader && <p className="text-12">{subHeader}</p>}
          </div>
        </div>
        {isCloseButton && fill === "" && href === "" ? (
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-24 w-24 items-center justify-center"
            data-testid="close-button"
          >
            <XIcon size={24} color="#191A0B" weight="bold" />
          </button>
        ) : href !== "" && fill === "" ? (
          <a
            href={href}
            className={`font-600 leading-[20px] text-fnp-500  ${isShowCartEmpty ? "flex text-14" : "underline decoration-dotted"} items-center gap-4 text-12`}
          >
            {viewText} {isShowCartEmpty && <CaretRightIcon size={20} weight="bold" />}
          </a>
        ) : (
          normalizedVariant === "info" &&
          !!fill && (
            <div className="w-40">
              <RadialProgress fill={fill} strokeWidth={strokeWidth} />
            </div>
          )
        )}
      </div>
    </div>
  );

  if (containerClasses) {
    return <div className={containerClasses}>{toastContent}</div>;
  }

  return toastContent;
};

Toast.propTypes = {
  variant: PropTypes.string.isRequired,
  subHeader: PropTypes.string,
  header: PropTypes.string.isRequired,
  isCloseButton: PropTypes.bool,
  isToast: PropTypes.bool.isRequired,
  onToastChange: PropTypes.func,
  duration: PropTypes.number,
  position: PropTypes.string,
  fill: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  strokeWidth: PropTypes.string,
  href: PropTypes.string,
  isDrawer: PropTypes.bool,
  isMobile: PropTypes.bool,
  isInline: PropTypes.bool,
  placement: PropTypes.oneOf(["top", "bottom", "auto"]),
  viewText: PropTypes.string,
  isShowCartEmpty: PropTypes.bool,
};

export default Toast;
